import { useCallback, useEffect, useId, useRef, useState } from "react";
import { exportCourseRequests } from "../api/course-requests-api";
import { useImportCourseRequests } from "../api/use-course-requests";
import type { CourseRequestImportResult } from "../types";

/** Backend enforces 5 MB; reject client-side before the round-trip. */
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ".csv,text/csv,application/vnd.ms-excel";

interface UploadCourseRequestsPanelProps {
  scheduleId: string;
  /** Renders the "replace all existing requests" checkbox when true. */
  showReplaceAllToggle?: boolean;
  /**
   * Invoked when the user explicitly acknowledges the success summary (e.g.
   * clicking "Review" in the wizard or "Done" in the modal). Receives the
   * import result so the parent can advance to its next state.
   *
   * NOTE: this is NOT auto-fired on a timer (WCAG 2.2.2). The success summary
   * stays visible until the user chooses to proceed or to re-upload.
   */
  onSuccess: (result: CourseRequestImportResult) => void;
  /**
   * Label for the success-state "advance" button. Defaults to "Done" — the
   * wizard variant overrides this to "Review" to mirror the prototype copy.
   */
  successActionLabel?: string;
  /** When provided, a "Skip" link is rendered below the panel. */
  onSkip?: () => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function isCsvFile(file: File): boolean {
  if (file.name.toLowerCase().endsWith(".csv")) return true;
  return file.type === "text/csv" || file.type === "application/vnd.ms-excel";
}

/**
 * `droppedRows` covers row-level guards (missing id, bad course id, …). Any
 * remaining gap between `totalRowsInFile` and (`importedRows + droppedRows`)
 * is attributable to dedup or other quiet skips — report it as "skipped" so
 * the math always balances for the operator.
 */
function computeSkipped(result: CourseRequestImportResult): number {
  const accounted = result.importedRows + result.droppedRows;
  return Math.max(0, result.totalRowsInFile - accounted);
}

export function UploadCourseRequestsPanel({
  scheduleId,
  showReplaceAllToggle = false,
  onSuccess,
  successActionLabel = "Done",
  onSkip,
}: UploadCourseRequestsPanelProps) {
  const inputId = useId();
  const replaceAllId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [replaceAll, setReplaceAll] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [result, setResult] = useState<CourseRequestImportResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = useCallback(async () => {
    setExportError(null);
    setIsExporting(true);
    try {
      await exportCourseRequests(scheduleId);
    } catch (err) {
      setExportError(
        err instanceof Error ? err.message : "Failed to export course requests.",
      );
    } finally {
      setIsExporting(false);
    }
  }, [scheduleId]);

  const importMutation = useImportCourseRequests(scheduleId);
  const isUploading = importMutation.isPending;

  // Track the in-flight upload so we can abort on unmount or new-file selection.
  // We thread the controller into the mutation via the apiClient's `signal`
  // hand-off. Aborted errors are swallowed in the catch so we don't show a
  // server-error toast for a user-initiated cancel.
  const abortRef = useRef<AbortController | null>(null);

  // Clean up any pending upload on unmount — no stale state update if the
  // user navigates away mid-flight.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  const acceptFile = useCallback((picked: File) => {
    setServerError(null);
    setResult(null);
    if (!isCsvFile(picked)) {
      setClientError("Only .csv files are accepted.");
      setFile(null);
      return;
    }
    if (picked.size > MAX_FILE_BYTES) {
      setClientError(
        `File exceeds the 5 MB limit (got ${formatBytes(picked.size)}).`,
      );
      setFile(null);
      return;
    }
    setClientError(null);
    setFile(picked);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isUploading) return;
    const picked = e.target.files?.[0];
    if (picked) acceptFile(picked);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (isUploading) return;
    const picked = e.dataTransfer.files?.[0];
    if (picked) acceptFile(picked);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (isUploading) return;
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleUpload = async () => {
    if (!file) return;
    setServerError(null);
    setResult(null);
    // Cancel any previous in-flight upload before starting a new one.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const r = await importMutation.mutateAsync({
        file,
        replaceAll,
        signal: controller.signal,
      });
      // If the request was aborted mid-flight we may still resolve — guard.
      if (controller.signal.aborted) return;
      setResult(r);
    } catch (err) {
      // Aborted by unmount or a new-file selection — silently drop.
      if (controller.signal.aborted) return;
      setServerError(
        err instanceof Error
          ? err.message
          : "Failed to upload course requests.",
      );
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
    }
  };

  const handleReupload = () => {
    setResult(null);
    setFile(null);
    setServerError(null);
    setClientError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const canUpload = !!file && !isUploading;

  // Success state: take over the panel with a dedicated summary.
  if (result) {
    const skipped = computeSkipped(result);
    return (
      <div data-testid="upload-course-requests-panel" className="space-y-4">
        <div
          role="status"
          data-testid="upload-success-summary"
          className="px-4 py-3 rounded-md bg-green-500/10 border border-green-500/30 text-green-300 space-y-3"
        >
          <div className="font-medium text-[13px]">Import complete</div>
          <div
            data-testid="upload-success-stats"
            className="grid grid-cols-3 gap-3"
          >
            <SuccessStat
              testId="upload-stat-students"
              value={result.distinctStudents}
              label="Students"
            />
            <SuccessStat
              testId="upload-stat-imported"
              value={result.importedRows}
              label="Course requests imported"
            />
            <SuccessStat
              testId="upload-stat-skipped"
              value={skipped + result.droppedRows}
              label="Skipped"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleReupload}
            data-testid="upload-reupload"
            className="bg-surface-2 hover:bg-surface text-text-soft border border-border-subtle rounded-md px-3.5 py-2 text-sm font-medium cursor-pointer"
          >
            Re-upload
          </button>
          <button
            type="button"
            onClick={() => onSuccess(result)}
            data-testid="upload-acknowledge"
            className="bg-brand hover:bg-brand-hover text-white border-none rounded-md px-[18px] py-[9px] font-semibold text-sm cursor-pointer"
          >
            {successActionLabel}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="upload-course-requests-panel" className="space-y-3">
      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={handleExport}
          disabled={isExporting}
          data-testid="export-course-requests"
          className="inline-flex items-center gap-1.5 text-[13px] text-text-faint hover:text-text-soft disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          {isExporting ? "Exporting…" : "Download current requests (CSV)"}
        </button>
      </div>
      {exportError && (
        <p role="alert" data-testid="export-error" className="text-xs text-red-400">
          {exportError}
        </p>
      )}

      {serverError && (
        <div
          role="alert"
          data-testid="upload-server-error"
          className="px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-sm text-red-400"
        >
          {serverError}
        </div>
      )}

      <div
        data-testid="upload-dropzone"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`border-2 border-dashed rounded-lg px-4 py-6 text-center transition-colors ${
          isDragging
            ? "border-brand bg-brand/5"
            : "border-border-subtle bg-page"
        } ${isUploading ? "pointer-events-none opacity-60" : ""}`}
      >
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={ACCEPTED_TYPES}
          onChange={handleFileChange}
          disabled={isUploading}
          data-testid="upload-file-input"
          className="sr-only"
        />
        <label
          htmlFor={inputId}
          className={`inline-flex flex-col items-center gap-1.5 ${
            isUploading ? "cursor-not-allowed" : "cursor-pointer"
          }`}
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="text-text-muted"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <span className="text-sm font-medium text-white">
            Drop CSV here or click to choose
          </span>
          <span className="text-xs text-text-faint">
            Max 5 MB · header row required
          </span>
        </label>
      </div>

      {file && (
        <div
          data-testid="upload-selected-file"
          className="flex items-center justify-between text-[13px] bg-page border border-border-subtle rounded-md px-3 py-2"
        >
          <div className="text-text-soft">
            <span
              className="font-medium text-white"
              data-testid="upload-file-name"
            >
              {file.name}
            </span>{" "}
            <span className="text-text-muted">({formatBytes(file.size)})</span>
          </div>
          <button
            type="button"
            onClick={() => {
              setFile(null);
              if (inputRef.current) inputRef.current.value = "";
            }}
            disabled={isUploading}
            data-testid="upload-clear-file"
            className="text-text-faint hover:text-text-soft text-xs disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Remove
          </button>
        </div>
      )}

      {clientError && (
        <p
          role="alert"
          data-testid="upload-client-error"
          className="text-xs text-red-400"
        >
          {clientError}
        </p>
      )}

      {showReplaceAllToggle && (
        <label
          htmlFor={replaceAllId}
          className="flex items-center gap-2 text-[13px] text-text-soft cursor-pointer"
        >
          <input
            id={replaceAllId}
            type="checkbox"
            checked={replaceAll}
            onChange={(e) => setReplaceAll(e.target.checked)}
            data-testid="upload-replace-all-toggle"
            className="accent-brand"
          />
          Replace all existing requests
        </label>
      )}

      <div className="flex items-center justify-between pt-1">
        {onSkip ? (
          <button
            type="button"
            onClick={onSkip}
            disabled={isUploading}
            data-testid="upload-skip-link"
            className="text-[13px] text-text-faint hover:text-text-soft disabled:opacity-50"
          >
            Skip for now
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={handleUpload}
          disabled={!canUpload}
          data-testid="upload-submit"
          aria-busy={isUploading || undefined}
          className="bg-brand hover:bg-brand-hover text-white border-none rounded-md px-[18px] py-[9px] font-semibold text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isUploading ? "Uploading…" : "Upload"}
        </button>
      </div>
    </div>
  );
}

interface SuccessStatProps {
  value: number;
  label: string;
  testId: string;
}

/**
 * Single-cell stat in the post-upload summary grid. Pulled into its own
 * component so the three callers above stay terse and we can A/B the label
 * styling in one place if the design evolves.
 */
function SuccessStat({ value, label, testId }: SuccessStatProps) {
  return (
    <div
      data-testid={testId}
      className="rounded-md bg-page/50 border border-green-500/20 px-3 py-2 flex flex-col"
    >
      <span className="text-xl font-semibold text-white leading-tight">
        {value}
      </span>
      <span className="text-[11px] uppercase tracking-wide text-green-300/80 mt-0.5">
        {label}
      </span>
    </div>
  );
}
